import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
// Iter-146 · Shared source of truth for the 6 canonical industries — same
// list appears in Signup, Settings › Company and the +Add Company dialog.
// Iter-188 · Signup now uses the 5-option founder-signed BUSINESS_TYPES
// list + Distribution / Dealership add-on checkboxes. The picked business
// type maps 1:1 to a legacy industry_type for Chart-of-Accounts seeding.
import { BUSINESS_TYPES } from "@/lib/industry-types";
import ShareCapitalForm, { emptyShareCapital, shareCapitalPayload } from "@/components/ShareCapitalForm";

// Same short bullets shown on the landing page — keeps signup ↔ landing in sync visually
const PLAN_BULLETS = {
  solo:         ["Accounting · GST-3B basic", "1 admin + 1 user", "1 GB storage · 200 emails/mo"],
  starter:      ["Full GST · P2P + O2C flows", "1 admin + 2 users", "5 GB storage · 500 emails/mo"],
  growth:       ["Inventory · TDS · E-Invoice", "1 admin + 5 users", "25 GB · 2,000 emails/mo"],
  professional: ["Procurement · Fixed Assets", "1 admin + 10 users", "100 GB · 10,000 emails/mo"],
  enterprise:   ["Tax Audit · Consolidation · MIS", "1 admin + 15 users · ∞ emails", "Manufacturing / Dealership add-ons"],
};
const PLAN_ORDER = ["solo", "starter", "growth", "professional", "enterprise"];
const BASE_BTS = BUSINESS_TYPES.map(({ v, l, sub, industry }) => ({ id: v, label: l, sub, industry }));

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export default function Signup() {
  const nav = useNavigate();
  const { signup } = useAuth();
  const [plans, setPlans] = useState([]);
  const [coaCounts, setCoaCounts] = useState(null);
  const [form, setForm] = useState({
    org_name: "", entity_type: "proprietorship",
    // Founder-signed base business type + legacy industry (for CoA seed)
    business_type: "trading_with_inventory", industry_type: "trading_inventory",
    full_name: "", email: "", password: "",
    plan: "starter",
    is_dealership: false,   // Dealership 360 add-on
    is_distribution: false, // Distribution add-on (van sales / route)
    org_type: "business",
    initial_gstin: "",
  });
  const [busy, setBusy] = useState(false);
  const [gstBusy, setGstBusy] = useState(false);
  const [shareCap, setShareCap] = useState(emptyShareCapital());
  const [gstInfo, setGstInfo] = useState(null);
  const [gstErr,  setGstErr]  = useState("");

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/plans/public`).then((r) => {
      const list = (r.data?.plans || []).map((p) => ({ ...p, bullets: PLAN_BULLETS[p.id] || [] }));
      list.sort((a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id));
      setPlans(list);
    }).catch(() => setPlans([]));
    axios.get(`${base}/api/signup-preview/coa-counts`).then((r) => setCoaCounts(r.data))
         .catch(() => setCoaCounts(null));
  }, []);

  const selected = useMemo(() => plans.find((p) => p.id === form.plan) || plans[0], [plans, form.plan]);
  const industryCount = coaCounts?.by_industry?.[form.industry_type];

  const verifyGstin = async (g) => {
    const gg = (g || "").trim().toUpperCase();
    if (gg.length !== 15) { setGstInfo(null); setGstErr(""); return; }
    setGstBusy(true); setGstErr(""); setGstInfo(null);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gstin/public-lookup?gstin=${gg}`);
      setGstInfo(r.data);
      if (!form.org_name && r.data.trade_name) {
        setForm((f) => ({ ...f, org_name: r.data.trade_name }));
      }
    } catch (e) {
      setGstErr(e?.response?.data?.detail || "GSTIN lookup failed");
    } finally { setGstBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (form.entity_type === "company" && shareCap.paid_up_capital !== "") payload.share_capital = shareCapitalPayload(shareCap);
      const res = await signup(payload);
      if (res?.share_capital_error) toast.error(`Share capital not posted: ${res.share_capital_error} — fix it in Settings → Shareholders`);
      toast.success("Account created — welcome!");
      nav("/app/dashboard");
    } catch (err) {
      // Iter-116 · Surface the real Pydantic / server error verbatim so
      // users can see exactly which field is invalid (email format, missing
      // org_name, entity_type mismatch, etc.) instead of a generic message.
      const raw = err?.response?.data?.detail;
      let msg = "Signup failed — please try again";
      if (Array.isArray(raw)) {
        // Pydantic validation error array → pick the first & make it human
        const first = raw[0] || {};
        const field = (first.loc || []).slice(1).join(".") || "field";
        msg = `${field}: ${first.msg || "invalid value"}`;
      } else if (typeof raw === "string") {
        msg = raw;
      } else if (err?.response?.status === 429) {
        msg = "Too many signup attempts. Wait 60 seconds and try again.";
      } else if (err?.message) {
        msg = err.message;
      }
      toast.error(msg, { duration: 7000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen md:grid md:grid-cols-2 dark bg-background text-foreground">
      <Toaster theme="dark" richColors />
      {/* Marketing pane — hidden on mobile */}
      <div className="hidden md:block relative overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.25), transparent 45%), radial-gradient(circle at 80% 30%, rgba(168,85,247,0.22), transparent 50%), radial-gradient(circle at 60% 80%, rgba(249,115,22,0.18), transparent 50%)" }} />
        <div className="relative z-10 p-10 lg:p-14 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-10 w-auto" />
            <div>
              <div className="font-heading font-bold text-lg text-logo-gradient">Zenith 360 ERP</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/60">India · AI-native</div>
            </div>
          </Link>
          <div className="space-y-6">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
              Set up your <span className="text-logo-gradient">company</span><br />in under 60 seconds.
            </h1>
            <ul className="space-y-3 text-base text-white/80">
              {[
                "Full India Chart of Accounts pre-loaded",
                "Head-office branch auto-filled from your GSTIN",
                "Blank customer / vendor / item lists — your data only",
                "AI assistant ready to draft your first entry",
                "Change plan or cancel anytime — no lock-in",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-1" />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">v1.4 · India · Finance-grade</div>
        </div>
      </div>

      {/* Mobile-first form pane */}
      <div className="flex flex-col min-h-screen md:min-h-0 md:items-center md:justify-center md:p-10 bg-background">
        {/* Mobile-only compact header */}
        <div className="md:hidden px-5 pt-5 pb-3 border-b border-border/40 sticky top-0 bg-background z-20">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-7 w-auto" />
            <div>
              <div className="font-heading font-bold text-sm text-logo-gradient">Zenith 360 ERP</div>
              <div className="text-[8px] font-mono uppercase tracking-widest text-foreground/60">India · AI-native</div>
            </div>
          </Link>
        </div>

        <form onSubmit={submit}
                className="w-full md:max-w-md px-5 py-5 md:p-0 space-y-4 md:space-y-5 pb-28 md:pb-0"
                data-testid="signup-form">
          <div className="space-y-1.5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent">// Get started</div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Create your company</h2>
            <div className="text-sm text-foreground/70">Already have one? <Link to="/login" className="text-accent hover:underline">Sign in</Link>.</div>
          </div>

          {/* Org type toggle */}
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Account type</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="signup-org-type-toggle">
              {[
                { id: "business", label: "Business", sub: "Run one company" },
                { id: "agency",   label: "Agency / CA firm", sub: "Manage many clients" },
              ].map((o) => {
                const on = form.org_type === o.id;
                return (
                  <button key={o.id} type="button" onClick={() => setForm({ ...form, org_type: o.id })}
                    data-testid={`signup-org-type-${o.id}`}
                    className={`text-left rounded-sm border p-3 min-h-[54px] transition-colors ${on ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}>
                    <div className="text-sm font-medium">{o.label}</div>
                    <div className="text-[10px] text-foreground/60 mt-0.5">{o.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
              {form.org_type === "agency" ? "Agency / Firm name" : "Business name"}
            </Label>
            <Input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                     className="rounded-sm mt-1.5 h-11" data-testid="signup-org-name"
                     placeholder={form.org_type === "agency" ? "Sharma & Co. Chartered Accountants" : "Acme Traders Pvt Ltd"} />
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Entity type</Label>
            <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
              <SelectTrigger className="rounded-sm mt-1.5 h-11" data-testid="signup-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="proprietorship">Proprietorship</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="company">Company (Pvt Ltd / Ltd)</SelectItem>
                <SelectItem value="llp">LLP</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.entity_type === "company" && (
            <div className="border border-foreground/10 rounded-sm p-3 space-y-2" data-testid="signup-share-capital">
              <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Share capital at formation</div>
              <ShareCapitalForm value={shareCap} onChange={setShareCap} />
            </div>
          )}

          {form.org_type === "business" && (
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                Business type <span className="normal-case tracking-normal text-muted-foreground">(controls which accounts get seeded + which features show up)</span>
              </Label>
              <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="signup-business-type-toggle">
                {BASE_BTS.map((o) => {
                  const on = form.business_type === o.id;
                  return (
                    <button key={o.id} type="button"
                      onClick={() => setForm({ ...form, business_type: o.id, industry_type: o.industry })}
                      data-testid={`signup-business-type-${o.id}`}
                      className={`text-left rounded-sm border p-3 min-h-[64px] transition-colors ${on ? "border-accent bg-accent/10" : "border-border hover:border-accent/40"}`}>
                      <div className="text-sm font-medium">{o.label}</div>
                      <div className="text-[10px] text-foreground/60 mt-0.5 leading-tight">{o.sub}</div>
                    </button>
                  );
                })}
              </div>
              {industryCount != null && (
                <div className="mt-2 p-2.5 rounded-sm border border-emerald-500/25 bg-emerald-500/5 flex items-center justify-between gap-3"
                     data-testid="signup-coa-preview">
                  <div className="text-[11px] text-foreground/80">
                    You'll start with <b className="text-emerald-500">{industryCount + (form.is_dealership ? (coaCounts.dealership_addon_accounts || 0) : 0)} accounts</b>
                    {form.is_dealership && <> (incl. <b>{coaCounts.dealership_addon_accounts}</b> dealership add-on)</>}
                    · {coaCounts.total - industryCount} skipped for your industry.
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                </div>
              )}
              {/* Add-on toggles — layered on top of the base business type */}
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="signup-addons">
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-sm border border-border hover:border-accent/40 transition-colors" data-testid="signup-distribution-label">
                  <input type="checkbox" checked={!!form.is_distribution}
                           onChange={(e) => setForm({ ...form, is_distribution: e.target.checked })}
                           className="mt-0.5" data-testid="signup-distribution" />
                  <span>
                    <span className="text-sm font-medium">Distribution add-on</span>
                    <span className="block text-[10px] text-foreground/60 mt-0.5">Van sales, route planning, beat-plans + FMCG-style claims / rebates.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-sm border border-border hover:border-accent/40 transition-colors" data-testid="signup-dealership-label">
                  <input type="checkbox" checked={!!form.is_dealership}
                           onChange={(e) => setForm({ ...form, is_dealership: e.target.checked })}
                           className="mt-0.5" data-testid="signup-dealership" />
                  <span>
                    <span className="text-sm font-medium">Dealership 360 add-on</span>
                    <span className="block text-[10px] text-foreground/60 mt-0.5">Vehicle master · Booking → Delivery · Insurance / RTO · Workshop tickets · OEM claims.</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Your full name</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                       className="rounded-sm mt-1.5 h-11" data-testid="signup-full-name" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Email</Label>
              <Input type="email" inputMode="email" autoComplete="email"
                       required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="rounded-sm mt-1.5 h-11" data-testid="signup-email" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
              Primary GSTIN <span className="normal-case tracking-normal text-muted-foreground">(optional — auto-verify)</span>
            </Label>
            <Input value={form.initial_gstin || ""}
                     onChange={(e) => setForm({ ...form, initial_gstin: e.target.value.toUpperCase() })}
                     onBlur={(e) => verifyGstin(e.target.value)}
                     maxLength={15} placeholder="27BVFPB3852E1ZV"
                     className="rounded-sm mt-1.5 h-11 font-mono uppercase"
                     autoCapitalize="characters" autoCorrect="off"
                     data-testid="signup-initial-gstin" />
            {gstBusy && <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifying with GSTN…</p>}
            {gstErr && <p className="text-[10px] text-red-500 mt-1" data-testid="signup-gstin-err">{gstErr}</p>}
            {gstInfo && !gstBusy && (
              <div className="mt-2 p-2.5 rounded-sm border border-emerald-500/25 bg-emerald-500/5 text-[11px] space-y-0.5" data-testid="signup-gstin-preview">
                <div className="flex items-center gap-1.5 text-emerald-500 font-medium">
                  <CheckCircle2 className="w-3 h-3" /> GSTIN verified {gstInfo.source === "gst_portal" ? "(gst.gov.in)" : "(offline)"}
                </div>
                <div className="text-foreground"><b>Legal name:</b> {gstInfo.legal_name || "—"}</div>
                {gstInfo.trade_name && gstInfo.trade_name !== gstInfo.legal_name &&
                  <div className="text-foreground"><b>Trade name:</b> {gstInfo.trade_name}</div>}
                <div className="text-foreground/70"><b>State:</b> {gstInfo.state} · <b>PAN:</b> {gstInfo.pan}</div>
                {gstInfo.address && <div className="text-foreground/60 text-[10px] truncate" title={gstInfo.address}>{gstInfo.address}</div>}
              </div>
            )}
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Password</Label>
            <Input type="password" required minLength={6} autoComplete="new-password"
                     value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className="rounded-sm mt-1.5 h-11" data-testid="signup-password" />
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Plan</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger className="rounded-sm mt-1.5 h-auto min-h-[46px]" data-testid="signup-plan">
                <SelectValue placeholder="Pick a plan" />
              </SelectTrigger>
              <SelectContent className="max-h-96">
                {plans.length === 0 && <SelectItem value="starter" disabled>Loading plans…</SelectItem>}
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`signup-plan-opt-${p.id}`}>
                    <div className="flex flex-col leading-tight">
                      <span className="font-medium">{p.name} — {inr(p.price_inr)}/mo</span>
                      <span className="text-[10px] text-muted-foreground">
                        Admin + {(p.bundled_users || 1) - 1} users · {(PLAN_BULLETS[p.id] || [])[0]}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <div className="mt-2 p-3 rounded-sm border border-accent/25 bg-accent/5 text-xs space-y-1" data-testid="signup-plan-summary">
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold text-sm">{selected.name}</span>
                  <span className="font-mono text-accent">{inr(selected.price_inr)}/mo</span>
                </div>
                <ul className="space-y-0.5 text-foreground/75 mt-1">
                  {(PLAN_BULLETS[selected.id] || []).map((b) => (
                    <li key={b} className="flex gap-1.5"><CheckCircle2 className="w-3 h-3 text-accent shrink-0 mt-0.5" />{b}</li>
                  ))}
                </ul>
                <div className="text-[10px] text-foreground/50 pt-1">Prices exclusive of 18% GST. 14-day free trial.</div>
              </div>
            )}
          </div>

          {form.org_type === "business" && false && (
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-sm border border-border hover:border-accent/40 transition-colors" data-testid="signup-dealership-label-old">
              <input type="checkbox" checked={!!form.is_dealership} onChange={(e) => setForm({ ...form, is_dealership: e.target.checked })} className="mt-0.5" data-testid="signup-dealership-old" />
              <span>
                <span className="text-sm font-medium">This is a Dealership business</span>
                <span className="block text-[10px] text-foreground/60 mt-0.5">Activates Dealership 360 (bookings, allocation, dispatch, RTO, insurance, NPS).</span>
              </span>
            </label>
          )}

          {form.org_type === "agency" && (
            <div className="p-3 rounded-sm border border-accent/25 bg-accent/5 text-xs text-foreground/80" data-testid="signup-agency-note">
              <div className="font-medium text-foreground">Agency account setup</div>
              <div className="mt-1 text-foreground/70 leading-relaxed">
                You'll land in an <b>Agency Panel</b> after signup. Add each client company from there — each gets its own books, users and GSTINs.
              </div>
            </div>
          )}

          {/* Desktop submit — inline */}
          <Button type="submit" disabled={busy}
                    className="hidden md:flex w-full rounded-sm bg-logo-gradient hover:opacity-95 text-white font-medium border-0 h-11" data-testid="signup-submit">
            {busy ? "Creating…" : (form.org_type === "agency" ? "Create agency account" : "Create company")}
          </Button>

          <div className="text-[10px] text-foreground/50 text-center leading-relaxed">
            By continuing you agree to our Terms. 14-day trial · no credit card · export your data any time.
          </div>
        </form>

        {/* Mobile-only sticky submit */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 p-3 bg-background/95 backdrop-blur border-t border-border">
          <Button type="submit" form={undefined} onClick={submit} disabled={busy}
                    className="w-full rounded-sm bg-logo-gradient hover:opacity-95 text-white font-medium border-0 h-12 text-base" data-testid="signup-submit-mobile">
            {busy ? "Creating…" : (form.org_type === "agency" ? "Create agency account" : "Create company")}
          </Button>
        </div>
      </div>
    </div>
  );
}
